import React, { useEffect, useState } from "react";
import Axios from "axios";
import * as constants from "../constants";
import Documents from "../services/Documents";
import { useLocation, useParams } from "react-router";
import queryString from "query-string";
import { BookOpen, Hash, Play, Flag, Loader2 } from "lucide-react";

const UOSSummary = () => {
  const location = useLocation();
  const { programId } = queryString.parse(location.search);
  const [programUsedId, setProgramUsedId] = useState(programId);
  const { id } = useParams();

  const [subjectSummary, setSubjectSummary] = useState(undefined);
  const [loading, setLoading] = useState(true);

  const resourcesCardHeaderSettings = {
    headingText: "Resources",
    description: "available to download",
    objectType: 23,
  };

  const documentsCardHeaderSettings = {
    headingText: "My Assignments",
    description: "submitted so far",
    objectType: 13,
    objectId: id,
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await Axios.get(
          constants.BASEURL +
            "Student/SubjectUOSSummary/" +
            id +
            "?objectType=uos&programId=" +
            programUsedId
        );
        const data = response.data;
        setSubjectSummary(data);
        setProgramUsedId(data?.enrolmentId);
      } catch (error) {
        setSubjectSummary(undefined);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, programId]);

  const formatDateValue = (dateStr) => (dateStr && dateStr.trim() ? dateStr : "Not set");

  const getStatusMeta = (s) => {
    if (!s) return { label: "Unknown", cls: "bg-gray-50 text-gray-700 border-gray-200" };
    if (s.finished) return { label: "Completed", cls: "bg-green-50 text-green-700 border-green-200" };
    if (s.started) return { label: "In progress", cls: "bg-blue-50 text-blue-700 border-blue-200" };
    return { label: "Not started", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  };

  const MetaPill = ({ children }) => (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border border-gray-200 bg-gray-50 text-gray-700">
      {children}
    </span>
  );

  const renderDocuments = (documentType) => {
    if (documentType === 1) {
      const optionSettings = resourcesCardHeaderSettings;
      optionSettings.objectId = id;
      return <Documents options={resourcesCardHeaderSettings} />;
    }
    return <Documents options={documentsCardHeaderSettings} />;
  };

  const renderSubjectSummary = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading summary…
        </div>
      );
    }

    if (!subjectSummary) {
      return <p className="text-sm text-gray-500">No summary found.</p>;
    }

    const status = getStatusMeta(subjectSummary);

    return (
      <>
        {/* Code + Name */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 text-xs font-medium">
              <Hash className="w-3.5 h-3.5" />
              {subjectSummary?.subjectCode || "N/A"}
            </div>

            <h2 className="mt-2 text-base font-semibold text-gray-900 truncate">
              {subjectSummary?.subjectName || "Unit of Study"}
            </h2>

            {/* Simple pills: Status + Core + Optional */}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${status.cls}`}
              >
                {status.label}
              </span>

              {subjectSummary?.subjectType ? (
                <MetaPill>{subjectSummary.subjectType}</MetaPill>
              ) : null}

              {typeof subjectSummary?.compulsory === "boolean" ? (
                <MetaPill>{subjectSummary.compulsory ? "Compulsory" : "Optional"}</MetaPill>
              ) : null}
            </div>
          </div>

          <div className="hidden sm:flex w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5 text-blue-600" />
          </div>
        </div>

        {/* Dates */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
              <Play className="w-4.5 h-4.5 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500">Start date</p>
              <p className="text-sm font-semibold text-gray-900 truncate">
                {formatDateValue(subjectSummary?.startDate)}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <Flag className="w-4.5 h-4.5 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500">End date</p>
              <p className="text-sm font-semibold text-gray-900 truncate">
                {formatDateValue(subjectSummary?.endDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Optional: due date */}
        {subjectSummary?.dueDateForAssessment?.trim?.() ? (
          <div className="mt-3">
            <p className="text-xs text-gray-500">Assessment due</p>
            <p className="text-sm font-semibold text-gray-900">
              {subjectSummary.dueDateForAssessment}
            </p>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        {/* Summary Card */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-md overflow-hidden">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Unit of Study Summary</h3>
              <p className="text-xs text-gray-500 mt-0.5">Key details for this unit</p>
            </div>
          </div>

          <div className="px-4 sm:px-5 py-4">{renderSubjectSummary()}</div>
        </div>

        {/* Resources */}
        {id ? (
          <div className="mt-5 bg-white border border-gray-200 rounded-xl shadow-md p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-base font-semibold text-gray-900">Resources</h4>
                <p className="text-xs text-gray-500">Available to download</p>
              </div>
            </div>
            {renderDocuments(1)}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UOSSummary;
